import { useEffect, useState } from 'react';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Text, Group, Avatar, Badge } from '@repletoryreach/sdk-ui';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { initials } from '@repletoryreach/sdk-utils/format';
import { useT } from '../../../i18n/I18nProvider';
import { LOCALES, type Locale } from '../../../i18n/catalog';
import { localeCoverage } from '../../../i18n/coverage';
import { useLocalePreference } from '../../../lib/useLocalePreference';

const NAME_MAX = 80;

export default function SettingsProfilePage() {
  const t = useT();
  const { user, refresh } = useAuth();
  // §4 item 2 and settings-appearance §4 item 1: this page had its own
  // hardcoded locale list and wrote only the server copy, while Appearance
  // wrote only the client one. One hook now writes both, from one list.
  const { locale, setLocale } = useLocalePreference();

  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  /*
   * §4 item 1: `useState(user?.display_name ?? '')` runs ONCE, and
   * `AuthProvider` fills `user` asynchronously. On a hard refresh straight
   * onto this page the initialiser won forever, so the field stayed blank and
   * saving wrote that emptiness back over a real name.
   */
  useEffect(() => {
    setDisplayName(user?.display_name ?? '');
  }, [user?.display_name]);

  // §4 item 4: the Save button was always enabled, even with nothing changed.
  const dirty = (user?.display_name ?? '') !== displayName;

  async function save(e?: React.FormEvent) {
    e?.preventDefault();
    const name = displayName.trim();
    // §4 item 3: no trim, no length check; an empty string was a valid submit.
    if (name.length === 0) { setError(t('settings.profile.nameRequired')); return; }
    if (name.length > NAME_MAX) { setError(t('settings.profile.nameTooLong', { max: NAME_MAX })); return; }

    setBusy(true);
    try {
      await identity.updateProfile({ display_name: name, locale });
      await refresh();
      notifySuccess(t('settings.profile.toast.saved'), t('settings.profile.title'));
    } catch (err) {
      // §4 item 8: the two toasts had different titles, and the error one
      // reached across into the contacts namespace.
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('settings.profile.title'));
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    setResending(true);
    try {
      await identity.resendVerification(user?.email ?? '');
      notifySuccess(t('settings.profile.verification.sent', { email: user?.email ?? '' }), t('settings.profile.title'));
    } catch (err) {
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('settings.profile.title'));
    } finally {
      setResending(false);
    }
  }

  return (
    <Card withBorder>
      {/* §4 item 5: three inputs in a Stack, so Enter did nothing. */}
      <form onSubmit={save}>
        <Stack gap="md">
          <Group gap="sm" align="center">
            {/* §4 item 7: a page called Profile with no visual identity. */}
            <Avatar size="lg" radius="xl">{initials(user?.display_name || user?.email || '?')}</Avatar>
            <Stack gap={0}>
              <Title order={4}>{t('settings.profile.title')}</Title>
              <Text role="caption" c="var(--rr-color-text-muted)">{user?.email}</Text>
            </Stack>
          </Group>

          <Stack gap={4}>
            <TextInput
              label={t('common.email')}
              // §4 item 6: a disabled field that explained nothing.
              description={t('settings.profile.emailLocked')}
              value={user?.email ?? ''}
              disabled
            />
            <Group gap="xs" align="center">
              {user?.email_verified ? (
                <Badge size="xs" color="green" variant="light">{t('settings.profile.verified')}</Badge>
              ) : (
                <>
                  <Badge size="xs" color="orange" variant="light">{t('settings.profile.unverified')}</Badge>
                  <Button size="xs" variant="subtle" type="button" loading={resending} onClick={resendVerification}>
                    {t('settings.profile.resendVerification')}
                  </Button>
                </>
              )}
            </Group>
          </Stack>

          <TextInput
            // §4 item 9: the label borrowed a key from the phone-numbers table.
            label={t('settings.profile.displayName')}
            description={t('settings.profile.displayNameHint')}
            error={error}
            value={displayName}
            onChange={(e) => { setDisplayName(e.currentTarget.value); setError(''); }}
            maxLength={NAME_MAX}
            required
          />

          <Select
            label={t('common.language')}
            // §4 item 10: the hint sat below the control as a loose caption
            // rather than being attached to it.
            description={t('settings.profile.languageHint')}
            value={locale}
            onChange={(e) => setLocale(e.currentTarget.value as Locale)}
            data={LOCALES.map((l) => {
              const pct = localeCoverage(l.code);
              return {
                value: l.code,
                label: pct === 100 ? l.label : t('shell.locale.partial', { label: l.label, percent: pct })
              };
            })}
          />

          <div>
            <Button type="submit" loading={busy} disabled={!dirty}>{t('action.saveChanges')}</Button>
          </div>
        </Stack>
      </form>
    </Card>
  );
}
